import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Gavel, Check, Repeat, ArrowUpRight } from 'lucide-react';
import { getNotifications, markNotificationsRead } from '../lib/api';

const NOTIF_ICONS = { accepted: Check, settled: Repeat };
const iconFor = (t) => NOTIF_ICONS[t] || Gavel;
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'bid', label: 'Bids' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'settled', label: 'Settled' },
];

const timeAgo = (iso) => {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await getNotifications();
        setItems(d.items || []);
        await markNotificationsRead();
      } catch (e) { console.error('Failed to load notifications', e); } finally { setLoading(false); }
    })();
  }, []);

  const shown = items.filter((n) => filter === 'all' || n.type === filter);

  return (
    <div className="min-h-screen bg-farmway-green pt-28">
      <div className="container-fw max-w-3xl pb-24">
        <span className="eyebrow">Alerts</span>
        <h1 className="mt-3 flex items-center gap-3 display-xl text-4xl text-farmway-cream sm:text-5xl">
          <Bell className="h-8 w-8 text-farmway-gold" /> Notifications
        </h1>

        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`rounded-full px-5 py-2 font-display text-xs font-bold uppercase tracking-wider transition-all ${filter === f.key ? 'bg-farmway-gold text-farmway-green' : 'border border-white/15 text-farmway-cream/70 hover:text-farmway-cream'}`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
          {loading ? (
            <p className="px-6 py-10 text-farmway-cream/50">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="px-6 py-16 text-center text-farmway-cream/50">Nothing here yet.</p>
          ) : (
            shown.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <Link key={n.id} to={n.link || '/dashboard'}
                  className="flex items-start gap-4 border-t border-white/10 px-6 py-5 transition-colors first:border-t-0 hover:bg-white/5">
                  <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-farmway-gold/15 text-farmway-gold">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-display font-bold text-farmway-cream">{n.title}</p>
                      <span className="shrink-0 text-xs text-farmway-cream/40">{timeAgo(n.created_at)}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-farmway-cream/70">{n.body}</p>
                  </div>
                  <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-farmway-cream/30" />
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
